<script setup lang="ts">
import { Enums } from "@/enums";
import { fakes } from "@/fakes";
import { PV } from "@/widgets";
import { computed } from "vue";
import TrashIcon from "~icons/mdi/trash-outline";
import BoxIcon from "~icons/solar/box-outline";
import type { ProvisionSpec } from "../../common/types.common";
import { requireds, v } from "../../common/validation.common";
import { defineActions, defineState } from "../common";
import { FormController } from "../controller";
import { Buttons } from "../icons";
import { FormInputNumeric, FormInputSelect, FormInputText } from "../input";
import { FormHook, FormMessage, FormPanel, FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = ProvisionSpec[];
interface PropsType extends BaseProps<ModelType> {
    toggleable?: boolean;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    toggleable: false,
    value: undefined,
});

const model = defineModel<ModelType>({
    required: true,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

const form = new FormController<Partial<ModelType[number]>>({
    name: undefined,
    status: "C",
    quantity: 1,
    notes: undefined,
});

const validators = form.validators({
    name: requireds.string,
    status: requireds.options(Enums.ProvisionSpecStatusEnum.values),
    quantity: requireds.number,
});

form.onSubmit(async () => {
    if (await form.isNotValid()) {
        return;
    }

    const data = await v.parseAsync(validators.schema, form.models);

    model.value.push({
        name: data.name,
        status: data.status,
        quantity: data.quantity,
        notes: form.models.notes,
    });

    form.reset();
});

function onRemove(index: number) {
    model.value.splice(index, 1);
}

const showScramble = computed(() => import.meta.env.MODE !== "production");
</script>

<template>
    <FormPanel class="overflow-x-hidden" :param="props">
        <template #header>
            <BoxIcon />
            <div class="font-semibold text-gray-600 flex flex-row items-center gap-2">
                {{ props.label ?? "Permintaan Barang" }}
                <Buttons.BtnRandom v-if="showScramble" @click="form.scramble" />
            </div>
        </template>

        <template #icons>
            <span class="inline-flex flex-row">
                <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
            </span>
        </template>

        <FormHook v-if="state.editMode" @submit="form.submit">
            <template #input="{ formId }">
                <div class="flex flex-col gap-3">
                    <div class="flex flex-col md:flex-row items-start justify-between gap-3">
                        <FormInputSelect
                            v-model="form.models.status"
                            :attachReset="form.attachReset('C' as const)"
                            :attachScramble="form.attachScramble<typeof form.models.status>(fakes.random.selectFn)"
                            :attachValidate="form.attachValidate(validators.fields.status)"
                            class="w-full md:w-3/12"
                            :clearable="false"
                            embedMode="embed"
                            :formId="formId"
                            label="Penggunaan"
                            :options="Enums.ProvisionSpecStatusEnum.select"
                            required
                        />
                        <FormInputText
                            v-model="form.models.name"
                            :attachReset="form.attachReset()"
                            :attachScramble="form.attachScramble(fakes.item.name)"
                            :attachValidate="form.attachValidate(validators.fields.name)"
                            class="w-full md:w-7/12"
                            embedMode="embed"
                            :formId="formId"
                            label="Nama"
                            mode="create"
                            placeholder="Nama / Jenis Barang"
                            required
                        />
                        <FormInputNumeric
                            v-model="form.models.quantity"
                            :attachReset="form.attachReset(1)"
                            :attachValidate="form.attachValidate(validators.fields.quantity)"
                            class="w-full md:w-2/12"
                            :clearable="false"
                            embedMode="embed"
                            :formId="formId"
                            :formSubmit="true"
                            label="Jumlah"
                            mode="create"
                            placeholder="Jumlah"
                            required
                        />
                    </div>
                    <div class="flex flex-row items-end justify-between gap-3">
                        <FormInputText
                            v-model="form.models.notes"
                            :attachReset="form.attachReset()"
                            :attachScramble="form.attachScramble(fakes.random.phrase)"
                            class="grow"
                            embedMode="embed"
                            :formId="formId"
                            label="Catatan"
                            mode="create"
                            placeholder="Catatan Tambahan"
                        />
                        <PV.Button class="min-w-10 w-1/12 shadow-xs" :form="formId" icon="pi pi-plus" type="submit" />
                    </div>
                </div>
            </template>
        </FormHook>

        <div class="w-full overflow-auto">
            <table class="w-full border mt-4">
                <thead class="rns-table-header">
                    <tr class="bg-gray-800 text-white">
                        <th class="w-2/12">Penggunaan</th>
                        <th class="w-4/12">Nama</th>
                        <th class="w-1/12">Jumlah</th>
                        <th class="w-4/12">Catatan</th>
                        <th class="rns-table-header w-1/12"></th>
                    </tr>
                </thead>
                <tbody class="rns-table-content">
                    <tr v-for="(item, index) in model">
                        <td>{{ Enums.ProvisionSpecStatusEnum.label(item.status) }}</td>
                        <td>{{ item.name }}</td>
                        <td class="text-center">{{ item.quantity }}</td>
                        <td :colspan="state.editMode ? 1 : 2">
                            {{ item.notes }}
                        </td>
                        <td v-if="state.editMode" class="rns-table-content">
                            <div class="flex flex-row justify-center">
                                <TrashIcon class="text-red-500 hover:cursor-pointer" @click="() => onRemove(index)" />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="state.hasError" class="py-4">
            <FormMessage :state="state" />
        </div>
    </FormPanel>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-table-header th {
    @apply border border-gray-800 px-3 py-1;
}

.rns-table-content td {
    @apply border border-gray-400 px-3 py-1.5;
}
</style>
